import { useMatches } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

/**
 * App header breadcrumb (Figma AppHeader → Breadcrumb): "Rounds / All rounds".
 *
 * The trail is declared per route as `staticData.breadcrumb` (an array of `common` i18n keys)
 * and read back here from the deepest match — so a screen owns its own crumbs and this
 * component never hard-codes a route map. The augmentation that types `staticData` lives in
 * `src/router.ts`.
 *
 * NOTE: the design places a language switcher on this row. It is deliberately absent — language
 * is a profile preference applied at bootstrap (`PreferredLanguageSync`), not header chrome.
 *
 * The "/" separator is a literal glyph, not copy — it needs no translation.
 */
export function Breadcrumb() {
  const { t } = useTranslation("common");
  const matches = useMatches();

  // Deepest match that declares a trail wins — a child route's crumbs replace its parent's.
  const declared = matches
    .map((match) => match.staticData.breadcrumb)
    .findLast((crumbs) => crumbs !== undefined && crumbs.length > 0);

  // The rebuilt staff pages build the trail from every matched route instead: each route's own
  // `crumb` key, then the name its loader found (`{ crumb: "Cohorte 1" }`).
  const built = matches.flatMap((match) => {
    const steps: string[] = [];
    if (match.staticData.crumb) steps.push(t(match.staticData.crumb));
    const data: unknown = match.loaderData;
    if (data && typeof data === "object" && "crumb" in data) {
      const name = (data as { crumb: unknown }).crumb;
      if (typeof name === "string" && name !== "") steps.push(name);
    }
    return steps;
  });

  const trail = declared?.map((key) => t(key)) ?? built;

  if (trail.length === 0) return null;

  return (
    <nav aria-label={t("layout.breadcrumb")}>
      <ol className="flex flex-wrap items-center gap-xs text-sm-medium">
        {trail.map((label, index) => {
          const isLast = index === trail.length - 1;
          return (
            <li
              key={`${String(index)}-${label}`}
              className="flex items-center gap-xs"
            >
              {index > 0 ? (
                <span aria-hidden="true" className="text-(--text-disabled)">
                  /
                </span>
              ) : null}
              <span
                aria-current={isLast ? "page" : undefined}
                className={
                  isLast
                    ? "text-sm-semibold text-(--text-primary)"
                    : "text-(--text-secondary)"
                }
              >
                {label}
              </span>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
